import React, { useState, useEffect, useCallback, useMemo } from "react"
import { getPhotos } from "../api/photo"
import { Photo } from "../graphql/schema"
import { PhotoEl } from "../PhotoEl/PhotoEl"
import { apiPrefix } from "../api/api"
import { useInfiniteScroll } from "../hooks/useInfiniteScroll"
import { FlexH } from "../ui/ui"
import { getAssetsPath } from "../sharedTypes/responseData"

const shuffleArray = <T,>(array: T[]): T[] => {
  const newArr = [...array]
  for (let i = newArr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[newArr[i], newArr[j]] = [newArr[j], newArr[i]]
  }
  return newArr
}

export const PhotoListEl = ({
  columns,
  style,
}: {
  columns: number
  style?: React.CSSProperties
}) => {
  const [allPhotos, setAllPhotos] = useState<Photo[]>([])
  const [displayedPhotos, setDisplayedPhotos] = useState<Photo[]>([])

  const [hasMore, setHasMore] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string>("")

  useEffect(() => {
    const initFetch = async () => {
      setIsLoading(true)
      try {
        const photos = await getPhotos({ limit: 100, offset: 0 })
        console.log("Fetched photos:", photos.length)

        if (photos && photos.length > 0) {
          const shuffled = shuffleArray(photos)
          setAllPhotos(shuffled)

          const initialBatchSize = Math.min(columns * 2, shuffled.length)
          setDisplayedPhotos(shuffled.slice(0, initialBatchSize))

          if (initialBatchSize >= shuffled.length) {
            setHasMore(false)
          }
        } else {
          setHasMore(false)
        }
      } catch (err) {
        setError("Failed to fetch initial photos.")
        console.error(err)
      } finally {
        setIsLoading(false)
      }
    }

    initFetch()
  }, [columns])

  const loadImage = useCallback(async () => {
    if (isLoading || !hasMore || allPhotos.length === 0) return

    setIsLoading(true)

    try {
      setDisplayedPhotos((prev) => {
        const currentCount = prev.length
        const totalCount = allPhotos.length

        if (currentCount >= totalCount) {
          setHasMore(false)
          return prev
        }

        const nextBatchSize = columns
        const nextCount = Math.min(currentCount + nextBatchSize, totalCount)
        const nextBatch = allPhotos.slice(0, nextCount)
        if (nextCount >= totalCount) {
          setHasMore(false)
        }

        return nextBatch
      })
    } catch (err) {
      setError("Error loading more photos.")
    } finally {
      setIsLoading(false)
    }
  }, [isLoading, hasMore, allPhotos, columns])

  const observerTarget = useInfiniteScroll(loadImage, hasMore, isLoading)

  const columnsImages = useMemo(() => {
    const cols: Photo[][] = Array.from({ length: columns }, () => [])
    displayedPhotos.forEach((img, i) => {
      cols[i % columns].push(img)
    })
    return cols
  }, [displayedPhotos, columns])

  return (
    <div style={{ width: "100%", ...style }}>
      <FlexH
        style={{
          marginTop: "10rem",
          justifyContent: "center",
          alignItems: "flex-start",
          overflow: "unset",
        }}
      >
        {columnsImages.map((colImages, idx) => (
          <ImageStreamColumn
            key={idx}
            photoMaxSize={`min(36rem, ${80 / columns}vw)`}
            photoSize={`${80 / columns}vw`}
            images={colImages}
            style={{
              position: "relative",
              marginTop: `${20 * (idx % 2)}rem`,
            }}
          />
        ))}
      </FlexH>

      <div
        className="status-indicator"
        style={{ textAlign: "center", padding: "20px", clear: "both" }}
      >
        {isLoading && <p>加载中...</p>}
        {/* {!hasMore && displayedPhotos.length > 0 && <p></p>} */}
        {error && <p style={{ color: "red" }}>{error}</p>}
        {hasMore && (
          <div ref={observerTarget} style={{ height: "40px", width: "100%" }} />
        )}
      </div>
    </div>
  )
}

const ImageStreamColumn = ({
  photoMaxSize,
  photoSize,
  images,
  style,
}: {
  photoMaxSize: string
  photoSize: string
  images: Photo[]
  style?: React.CSSProperties
}) => {
  return (
    <div
      className="image-stream-column"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        ...style,
      }}
    >
      {images.map((photo) => (
        <PhotoEl
          key={photo.fileId}
          url={`${apiPrefix}/${getAssetsPath(photo.fileId || "")}`}
          title={photo.title || ""}
          description={photo.description || ""}
          maxSize={photoMaxSize}
          size={photoSize}
        />
      ))}
    </div>
  )
}

export default PhotoListEl
